/**
 * SpanTimeline - Gantt chart visualization for trace spans
 */

import { useState, useMemo } from 'react';
import { SmartContent } from './SmartContent';
import { TYPE_ICONS, TYPE_COLORS, TYPE_BAR_COLORS, formatDuration } from './spanConstants';
import { Box } from 'lucide-react';
import type { SpanPublic } from '@/types/traces';
import { useI18n } from '@/i18n';

interface SpanTimelineProps {
  spans: SpanPublic[];
  traceStartTime: string;
  traceDuration: number | null;
  truncated?: boolean;
  totalSpans?: number;
}

interface FlatSpan {
  span: SpanPublic;
  depth: number;
}

export function SpanTimeline({ spans, traceStartTime, traceDuration, truncated, totalSpans }: SpanTimelineProps) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const [selectedSpanId, setSelectedSpanId] = useState<string | null>(null);

  // Build tree and flatten to DFS order
  const flatSpans = useMemo(() => {
    const childrenMap = new Map<string, SpanPublic[]>();
    const rootSpans: SpanPublic[] = [];

    for (const span of spans) {
      if (!span.parent_span_id) {
        rootSpans.push(span);
      } else {
        const siblings = childrenMap.get(span.parent_span_id) || [];
        siblings.push(span);
        childrenMap.set(span.parent_span_id, siblings);
      }
    }

    const sortByTime = (a: SpanPublic, b: SpanPublic) =>
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
    rootSpans.sort(sortByTime);
    childrenMap.forEach(list => list.sort(sortByTime));

    const result: FlatSpan[] = [];
    function dfs(span: SpanPublic, depth: number) {
      result.push({ span, depth });
      const children = childrenMap.get(span.id) || [];
      for (const child of children) {
        dfs(child, depth + 1);
      }
    }
    for (const root of rootSpans) {
      dfs(root, 0);
    }
    return result;
  }, [spans]);

  // Compute time range
  const traceStartMs = new Date(traceStartTime).getTime();
  const totalDurationMs = traceDuration || Math.max(
    ...spans.map(s => {
      const endMs = s.end_time ? new Date(s.end_time).getTime() : new Date(s.start_time).getTime() + (s.duration || 0);
      return endMs - traceStartMs;
    }),
    1
  );

  // Time axis ticks (4-5 evenly spaced)
  const tickCount = totalDurationMs >= 1000 ? 5 : 4;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => (i / tickCount) * totalDurationMs);

  const selectedSpan = selectedSpanId ? spans.find(s => s.id === selectedSpanId) : null;

  return (
    <div className="space-y-2">
      {/* Time axis */}
      <div className="relative h-5 ml-[180px]">
        {ticks.map((ms, i) => (
          <span
            key={i}
            className="absolute text-[10px] text-gray-400 -translate-x-1/2"
            style={{ left: `${(ms / totalDurationMs) * 100}%` }}
          >
            {formatDuration(ms)}
          </span>
        ))}
      </div>

      {/* Span bars */}
      {flatSpans.map(({ span, depth }) => {
        const spanStartMs = new Date(span.start_time).getTime();
        const leftPct = Math.max(0, ((spanStartMs - traceStartMs) / totalDurationMs) * 100);
        const widthPct = Math.max(0.5, ((span.duration ?? 0) / totalDurationMs) * 100);
        const spanType = (span.type || 'general') as string;
        const barColor = TYPE_BAR_COLORS[spanType] || TYPE_BAR_COLORS.general;
        const isSelected = span.id === selectedSpanId;

        return (
          <div key={span.id} className="flex items-center h-7 group">
            {/* Label */}
            <div
              className="w-[180px] flex-shrink-0 text-xs font-mono truncate text-gray-700 pr-2 text-right"
              style={{ paddingLeft: depth * 12 }}
              title={span.name}
            >
              {span.name}
            </div>

            {/* Bar area */}
            <div className="flex-1 relative h-full">
              <div
                onClick={() => setSelectedSpanId(isSelected ? null : span.id)}
                className={`absolute top-1 h-5 rounded cursor-pointer transition-all ${barColor} ${
                  isSelected ? 'ring-2 ring-blue-500 ring-offset-1' : 'hover:brightness-90'
                }`}
                style={{
                  left: `${leftPct}%`,
                  width: `${Math.min(widthPct, 100 - leftPct)}%`,
                }}
                title={`${span.name} (${spanType}) — ${formatDuration(span.duration)}`}
              >
                {/* Duration label inside bar if wide enough */}
                {widthPct > 8 && (
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-medium truncate px-1">
                    {formatDuration(span.duration)}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Truncation warning */}
      {truncated && totalSpans && (
        <div className="text-[10px] text-amber-600 bg-amber-50 rounded px-2 py-1">
          {t.showingSpans.replace('{shown}', String(spans.length)).replace('{total}', String(totalSpans))}
        </div>
      )}

      {/* Hint when nothing selected */}
      {!selectedSpan && flatSpans.length > 0 && (
        <div className="text-xs text-gray-400 text-center py-2">{t.selectSpan}</div>
      )}

      {/* Detail panel */}
      {selectedSpan && <SpanDetailPanel span={selectedSpan} />}
    </div>
  );
}


function SpanDetailPanel({ span }: { span: SpanPublic }) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const spanType = (span.type || 'general') as string;
  const Icon = TYPE_ICONS[spanType] || Box;
  const colorClass = TYPE_COLORS[spanType] || TYPE_COLORS.general;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mt-2">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs">
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-medium ${colorClass}`}>
          <Icon className="w-3 h-3" />
          {spanType}
        </span>
        <span className="font-mono text-gray-800 truncate">{span.name}</span>
        {span.model && (
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{span.model}</span>
        )}
        <span className="flex-1" />
        <span className="text-gray-500">{formatDuration(span.duration)}</span>
        {span.usage?.total_tokens != null && (
          <span className="text-gray-500">{span.usage.total_tokens} {t.tokens}</span>
        )}
        {span.total_estimated_cost != null && (
          <span className="text-gray-500">${span.total_estimated_cost.toFixed(4)}</span>
        )}
      </div>

      {/* Content */}
      <div className="p-3 text-xs space-y-2 max-h-[300px] overflow-y-auto">
        {span.input != null && (
          <div>
            <p className="font-medium text-gray-600 mb-1">{t.spanInput}</p>
            <SmartContent data={span.input} compact />
          </div>
        )}
        {span.output != null && (
          <div>
            <p className="font-medium text-gray-600 mb-1">{t.spanOutput}</p>
            <SmartContent data={span.output} compact />
          </div>
        )}
        {span.usage && (
          <div className="flex gap-3 text-gray-500">
            {span.usage.prompt_tokens != null && <span>{t.tokenPrompt} {span.usage.prompt_tokens}</span>}
            {span.usage.completion_tokens != null && <span>{t.tokenCompletion} {span.usage.completion_tokens}</span>}
            {span.usage.total_tokens != null && <span>{t.tokenTotal} {span.usage.total_tokens}</span>}
          </div>
        )}
        {!span.input && !span.output && !span.usage && (
          <div className="text-gray-400 text-center py-2">{t.emptyContent}</div>
        )}
      </div>
    </div>
  );
}
